import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Stack,
    Title,
    Text,
    TextInput,
    Button,
    Paper,
    Alert,
    Group,
    Loader,
    Center,
    Card
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
    IconDownload,
    IconCertificate,
    IconAlertCircle,
    IconArrowLeft,
    IconTrophy,
    IconCheck
} from '@tabler/icons-react';
import { memberDashboard } from '../../services/memberDashboard';

const Certificate = () => {
    const [certificateData, setCertificateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const navigate = useNavigate();

    const form = useForm({
        initialValues: {
            first_name: '',
            last_name: '',
        },
        validate: {
            first_name: (value) => {
                if (!value) return 'First name is required';
                if (value.length < 2) return 'First name must be at least 2 characters';
                if (!/^[a-zA-Z\s]+$/.test(value)) return 'First name can only contain letters';
                return null;
            },
            last_name: (value) => {
                if (!value) return 'Last name is required';
                if (value.length < 2) return 'Last name must be at least 2 characters';
                if (!/^[a-zA-Z\s]+$/.test(value)) return 'Last name can only contain letters';
                return null;
            },
        },
    });

    useEffect(() => {
        loadCertificateData();
    }, []);

    const loadCertificateData = async () => {
        try {
            const data = await memberDashboard.getCertificate();
            setCertificateData(data);
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to load certificate information',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateCertificate = async (values) => {
        setGenerating(true);
        try {
            const result = await memberDashboard.generateCertificate(values);

            notifications.show({
                title: 'Success',
                message: result.message,
                color: 'green',
            });

            // Reload certificate data
            await loadCertificateData();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to generate certificate';
            notifications.show({
                title: 'Error',
                message,
                color: 'red',
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async () => {
        try {
            const response = await memberDashboard.downloadCertificate();

            // Create blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', '21-Day-Manifestation-Magic-Certificate.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            notifications.show({
                title: 'Success',
                message: 'Certificate downloaded successfully!',
                color: 'green',
            });
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to download certificate',
                color: 'red',
            });
        }
    };

    const handleBack = () => {
        navigate('/member/dashboard');
    };

    if (loading) {
        return (
            <Center h={400}>
                <Loader size="lg" />
            </Center>
        );
    }

    return (
        <Stack gap="md">
            {/* Header with Back Button */}
            <Group>
                <Button
                    variant="light"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={handleBack}
                >
                    Back to Dashboard
                </Button>
            </Group>

            {/* Main Content */}
            <Paper withBorder p="xl" radius="md">
                <Stack align="center" gap="lg">
                    {/* Icon and Title */}
                    <Group>
                        <IconCertificate size={48} color="var(--mantine-color-blue-6)" />
                        <Title order={2} ta="center">
                            Course Certificate
                        </Title>
                    </Group>

                    {/* Course Not Completed */}
                    {!certificateData?.completed && (
                        <Card withBorder p="lg" w="100%" maw={500}>
                            <Stack align="center" gap="md">
                                <IconAlertCircle size={64} color="var(--mantine-color-orange-6)" />
                                <Alert color="orange" w="100%">
                                    <Text size="sm" ta="center">
                                        {certificateData?.message}
                                    </Text>
                                </Alert>
                                <Text size="sm" c="dimmed" ta="center">
                                    Complete all classes in your curriculum to unlock your certificate of completion.
                                </Text>
                            </Stack>
                        </Card>
                    )}

                    {/* Course Completed - Certificate Already Generated */}
                    {certificateData?.completed && certificateData?.certificate_exists && (
                        <Card withBorder p="lg" w="100%" maw={500}>
                            <Stack align="center" gap="md">
                                {/*  <IconTrophy size={64} color="var(--mantine-color-gold-6)" />
 */}
                                <Title order={3} ta="center" c="green">
                                    🎉 Congratulations! 🎉
                                </Title>

                                <Text size="lg" fw={600} ta="center">
                                    {certificateData.first_name} {certificateData.last_name}
                                </Text>

                                <Text size="sm" ta="center" c="dimmed">
                                    You have successfully completed the Fearless February Brain Gym course!
                                </Text>

                                <Alert color="green" w="100%">
                                    <Group>
                                        {/* <IconCheck size={16} /> */}
                                        <Text size="sm">
                                            <IconCheck size={16} /> Your certificate has been generated and is ready for download.
                                        </Text>
                                    </Group>
                                </Alert>

                                <Button
                                    size="lg"
                                    leftSection={<IconDownload size={20} />}
                                    onClick={handleDownload}
                                    variant="gradient"
                                    gradient={{ from: 'blue', to: 'purple' }}
                                    fullWidth
                                >
                                    Download Certificate
                                </Button>
                            </Stack>
                        </Card>
                    )}

                    {/* Course Completed - Certificate Not Generated */}
                    {certificateData?.completed && !certificateData?.certificate_exists && (
                        <Card withBorder p="lg" w="100%" maw={500}>
                            <Stack gap="md">
                                <Stack align="center" gap="md">
                                    {/* <IconTrophy size={64} color="var(--mantine-color-gold-6)" />*/}
                                    <Title order={3} ta="center" c="green">
                                        🎉 Congratulations, {certificateData.member_name}! 🎉
                                    </Title>

                                    <Text size="sm" ta="center" c="dimmed">
                                        You have successfully completed the Fearless February Brain Gym course!
                                    </Text>
                                </Stack>

                                <Alert color="blue" w="100%">
                                    <Text size="sm" ta="center">
                                        Please enter your name as you want it to appear on your certificate.
                                    </Text>
                                </Alert>

                                <form onSubmit={form.onSubmit(handleGenerateCertificate)}>
                                    <Stack gap="md">
                                        <Group grow>
                                            <TextInput
                                                label="First Name"
                                                placeholder="Enter your first name"
                                                required
                                                {...form.getInputProps('first_name')}
                                            />
                                            <TextInput
                                                label="Last Name"
                                                placeholder="Enter your last name"
                                                required
                                                {...form.getInputProps('last_name')}
                                            />
                                        </Group>

                                        <Alert color="yellow" size="sm">
                                            <Text size="xs" ta="center">
                                                ⚠️ Once name written here, cannot be changed later
                                            </Text>
                                        </Alert>

                                        <Button
                                            type="submit"
                                            size="lg"
                                            loading={generating}
                                            leftSection={<IconCertificate size={20} />}
                                            variant="gradient"
                                            gradient={{ from: 'blue', to: 'purple' }}
                                            fullWidth
                                        >
                                            Generate My Certificate
                                        </Button>
                                    </Stack>
                                </form>
                            </Stack>
                        </Card>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default Certificate;